import React, { useEffect, useState } from "react";
import { DownloadIcon, ShareIcon } from "@heroicons/react/outline";

import ApiService from "../../services/ApiService";
import UserService from "../../services/UserService";
import { iconFor, TYPE_GRADIENT } from "./WorkGridCard";

function HeroAction({ icon: Icon, onClick, label, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="relative rounded-full bg-white/90 p-2 text-fairblue-600 shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-white"
    >
      <Icon className="h-5 w-5" />
      {children}
    </button>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-gray-900">{children || "—"}</dd>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleDateString();
}

/**
 * The work detail card (hero + body). Fetches the public work by grid; used
 * both as a full page (WorkPage) and inside the modal overlay.
 */
function WorkDetail({ grid }) {
  const [work, setWork] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const w = await ApiService.getPublicWork(grid);
        if (active) setWork(w);
      } catch (e) {
        console.error("Could not load work", grid, e);
      }
    })();
    return () => {
      active = false;
    };
  }, [grid]);

  if (!work) return null;

  const Icon = iconFor(work.type);
  const author = work?.creator?.author;
  const license = work?.license?.identifier || work.licence;
  const tags = Array.isArray(work.tags) ? work.tags : [];

  const share = async () => {
    const url = `${window.location.origin}/works/${work.grid || grid}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: work.title, url });
        return;
      } catch (e) {
        if (e.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-gray-900/5 shadow-sm">
      {/* hero */}
      <div
        className={`relative h-44 flex items-center justify-center ${TYPE_GRADIENT}`}
      >
        <Icon className="h-16 w-16 text-white/90" aria-hidden="true" />
        <div className="absolute top-4 right-4 flex gap-2">
          {UserService.isLoggedIn() && (
            <HeroAction
              icon={DownloadIcon}
              label="Download work"
              onClick={() =>
                ApiService.downloadPublicWork(work.grid || grid, work.filename)
              }
            />
          )}
          {work.grid && (
            <HeroAction
              icon={ShareIcon}
              label="Copy share link"
              onClick={share}
            >
              {copied && (
                <span className="absolute top-full right-0 mt-2 whitespace-nowrap rounded-md bg-black px-2 py-1 text-xs text-white">
                  Copied
                </span>
              )}
            </HeroAction>
          )}
        </div>
      </div>

      {/* body */}
      <div className="p-6 sm:p-8">
        <span className="text-xs font-semibold uppercase tracking-wide text-fairblue-500">
          {work.type}
        </span>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          {work.title}
        </h1>
        {work.description && (
          <p className="mt-3 max-w-3xl leading-relaxed text-gray-600">
            {work.description}
          </p>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-gray-100 pt-6 sm:grid-cols-2 lg:grid-cols-4">
          <Meta label="Author">
            {author?.url ? (
              <a href={author.url} className="text-fairblue-600 hover:underline">
                {author.pseudonym || author.firstName}
              </a>
            ) : (
              author?.pseudonym || author?.firstName
            )}
          </Meta>
          <Meta label="License">{license}</Meta>
          <Meta label="Created">{formatDate(work.creationDate)}</Meta>
          <Meta label="Registered">{formatDate(work.registrationDate)}</Meta>
        </dl>

        {tags.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {tags.map((tag, i) => (
              <span
                key={i}
                className="inline-block rounded-full bg-fairblue-100 px-3 py-1 text-sm font-medium text-fairblue-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkDetail;
